import React, { useEffect } from "react";
import { DockviewApi, DockviewPanelApi } from "dockview-react";
import { ReadableItem } from "./useReadAloud";

interface ReadAloudBarProps {
  voiceControls: {
    speed: number;
    voice: string;
    isReading: boolean;
    isPaused: boolean;
    pitch: number;
    align: boolean;
  };
  setVoiceControls: React.Dispatch<React.SetStateAction<ReadAloudBarProps["voiceControls"]>>;
  toggleReading: (items: ReadableItem[], onItemDone?: (key: string) => void) => void;
  skipItem: (items: ReadableItem[], onItemDone?: (key: string) => void) => void;
  items: ReadableItem[];
  onItemDone?: (key: string) => void;
  idPrefix?: string;
  containerApi?: DockviewApi;
  panelApi?: DockviewPanelApi;
}

export const ReadAloudBar: React.FC<ReadAloudBarProps> = ({
  voiceControls,
  setVoiceControls,
  toggleReading,
  skipItem,
  items,
  onItemDone,
  idPrefix = "",
  containerApi,
  panelApi,
}) => {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      // Only handle Space, ArrowRight, and 'a' keys
      if (e.key !== " " && e.key !== "ArrowRight" && e.key.toLowerCase() !== "a") return;

      // If panel APIs are provided, only handle when this panel is active
      if (containerApi && panelApi && containerApi.activePanel?.id !== panelApi.id) return;

      // Skip if user is typing in an input
      const tag = (document.activeElement as HTMLElement)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
      if ((document.activeElement as HTMLElement)?.isContentEditable) return;

      e.preventDefault();
      e.stopPropagation();

      if (e.key === " ") {
        toggleReading(items, onItemDone);
      } else if (e.key === "ArrowRight") {
        skipItem(items, onItemDone);
      } else if (e.key.toLowerCase() === "a") {
        setVoiceControls((prev) => ({ ...prev, align: !prev.align }));
      }
    };

    window.addEventListener("keydown", handler, { capture: true });
    return () => window.removeEventListener("keydown", handler, { capture: true } as any);
  }, [containerApi, panelApi, toggleReading, skipItem, setVoiceControls, items, onItemDone]);

  return (
    <div className="shrink-0">
      <form
        action="submit"
        onSubmit={(e) => {
          e.preventDefault();
          toggleReading(items, onItemDone);
        }}
        className="flex flex-col gap-2 bg-white dark:bg-neutral-800 z-10 w-full border-t border-black dark:border-neutral-600 p-1 shrink-0"
      >
        {/* Selectors row */}
        <div className="flex flex-row gap-2">
          <div className="form-group w-28">
            <label htmlFor={`${idPrefix}speech-speed`}>Speed:</label>
            <select
              id={`${idPrefix}speech-speed`}
              value={voiceControls.speed.toString()}
              onChange={(e) =>
                setVoiceControls((prev) => ({
                  ...prev,
                  speed: parseFloat(e.target.value),
                }))
              }
              className="border border-gray-300 rounded p-1 block w-full"
            >
              {[1.0, 1.1, 1.2, 1.3, 1.4, 1.5, 1.6, 1.7, 1.8, 1.9, 2.0].map((speed) => (
                <option key={speed} value={speed.toString()}>
                  {speed.toFixed(1)}x
                </option>
              ))}
            </select>
          </div>

          <div className="form-group w-40">
            <label htmlFor={`${idPrefix}speech-voice`}>Voice:</label>
            <select
              id={`${idPrefix}speech-voice`}
              value={voiceControls.voice}
              onChange={(e) =>
                setVoiceControls((prev) => ({
                  ...prev,
                  voice: e.target.value,
                }))
              }
              className="border border-gray-300 rounded p-1 block w-full"
            >
              {speechSynthesis.getVoices().map((voice) => (
                <option key={voice.name} value={voice.name}>
                  {voice.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Buttons row */}
        <div className="flex flex-row gap-2">
          <button
            className="btn btn-primary h-10 flex-1 sm:flex-none"
            type="button"
            onClick={(e) => {
              e.preventDefault();
              setVoiceControls((prev) => ({
                ...prev,
                align: !prev.align,
              }));
            }}
          >
            {voiceControls.align ? "No align" : "Align"}
          </button>

          <button className="btn btn-primary h-10 flex-1 sm:flex-none" type="submit">
            {voiceControls.isReading ? "Stop" : "Read"}
          </button>

          <button
            className="btn btn-primary h-10 flex-1 sm:flex-none"
            type="button"
            onClick={(e) => {
              e.preventDefault();
              skipItem(items, onItemDone);
            }}
          >
            Skip
          </button>
        </div>
      </form>
    </div>
  );
};
